import { EHeadingStyleVariant, ETextStyleVariant, Heading, Text } from '@outdoorsyco/bonfire';
import { FormattedMessage, useIntl } from 'react-intl';

import DestinationIcon from '@/assets/escapes/icons/destination.svg';
import RelaxIcon from '@/assets/escapes/icons/relax.svg';
import RvIcon from '@/assets/escapes/icons/rv.svg';
import { EVideoPlayerVariant, VideoPlayer } from '@/components/common/VideoPlayer';

type TStep = {
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
  title: string;
  description: string;
};

const Step = ({ Icon, title, description }: TStep) => (
  <div className="flex flex-1 gap-6 sm:flex-col">
    <div className="flex w-[60px] sm:w-auto">
      <Icon className="h-[48px] sm:h-[78px]" />
    </div>
    <div className="flex-1">
      <Text variant={ETextStyleVariant.MediumBold} className="mb-2 text-base md:text-[20px]">
        {title}
      </Text>
      <Text variant={ETextStyleVariant.SmallRegular}>{description}</Text>
    </div>
  </div>
);

export const HowItWorks = () => {
  const intl = useIntl();

  const steps: TStep[] = [
    {
      Icon: DestinationIcon,
      title: intl.formatMessage({ defaultMessage: 'Pick your resort destination', id: 'FiRgp4' }),
      description: intl.formatMessage({
        defaultMessage:
          'Choose from handpicked locations with resort-style amenities and scenic views.',
        id: '8mds7f',
      }),
    },
    {
      Icon: RvIcon,
      title: intl.formatMessage({ defaultMessage: 'Select your RV', id: 'HVDHPa' }),
      description: intl.formatMessage({
        defaultMessage:
          'Your spacious, fully equipped rig will be waiting at the resort with beds, showers, and modern comforts.',
        id: 'nb7Z2A',
      }),
    },
    {
      Icon: RelaxIcon,
      title: intl.formatMessage({ defaultMessage: 'Just show up & relax', id: 'IOZiAf' }),
      description: intl.formatMessage({
        defaultMessage:
          'Your RV is delivered to the resort, set up with camp chairs out and awning up, ready when you arrive.',
        id: 'qS+idI',
      }),
    },
  ];

  return (
    <div>
      <Heading
        variant={EHeadingStyleVariant.H3Alt}
        className="mb-8 not-italic font-normal text-left text-850 md:text-900 leading-[35px]">
        <FormattedMessage
          defaultMessage="One simple booking. Your RV meets you there."
          id="eTcG/W"
        />
      </Heading>
      <div className="flex flex-col gap-8 sm:flex-row">
        {steps.map((step, index) => (
          <Step key={index} {...step} />
        ))}
      </div>
      <div className="hidden md:block md:mt-16">
        <VideoPlayer
          src="https://www.youtube.com/embed/_RmTM1KRxAY"
          title={intl.formatMessage({
            defaultMessage: 'Introducing Outdoorsy Escapes',
            id: 'h4UloY',
          })}
          className="mx-auto lg:w-[60%]"
          variant={EVideoPlayerVariant.YOUTUBE}
        />
      </div>
    </div>
  );
};
